import { AnyAction } from "@reduxjs/toolkit";
import BonusChartData from "../../Models/BonusChartData";
import { FeeConfig, WithdrawalFeeType } from "../../Models/Crypto";
import { DepositResponse, IDeposit } from "../../Models/Deposit";
import { ISwap } from "../../Models/Swap";
import UserWallet, { UserCryptoWallet } from "../../Models/UserWallet";
import axios from "../AxiosInstance";
import UtilsService from "../UtilsService";
import { TransfertAccount } from "../../Models/Withdraw";

const map = (item: any): UserWallet => {
    const user_wallet = new UserWallet(item.crypto, item.solde, item.usd_price);

    user_wallet.crypto = item.crypto;
    user_wallet.solde = item.solde;
    user_wallet.usd_price = item.usd_price;

    return user_wallet;
};

const mapBonusChartData = (item: any): BonusChartData => {
    const bonus_chart_data = new BonusChartData(
        item.roi_stacking,
        item.roi_knt,
        item.rank_bonus,
        item.pool_bonus,
        item.affiliation,
    );
    return bonus_chart_data;
};

const getUserWallet = async (
    no_personal = false,
): Promise<Array<UserCryptoWallet>> => {
    return axios
        .get(`/user-wallet`)
        .then(({ data }) => {
            const wallets: UserCryptoWallet[] = data as UserCryptoWallet[];

            const cryptos = wallets.filter(
                (elt: UserCryptoWallet) => elt.type === "crypto",
            );

            const fiats = wallets.filter(
                (elt: UserCryptoWallet) =>
                    elt.type === "fiat" && !elt.is_personal,
            );

            const personals = wallets.filter(
                (elt: UserCryptoWallet) =>
                    elt.type === "fiat" && !!elt.is_personal,
            );

            return [...fiats, ...personals, ...cryptos];
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getUserWalletWithNetwork = async (): Promise<Array<UserCryptoWallet>> => {
    return axios
        .get(`/user-wallet/with-networks`)
        .then(({ data }) => {
            const wallets: UserCryptoWallet[] = data as UserCryptoWallet[];

            return wallets;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getByUserFromAdmin = async (
    uid: string,
): Promise<Array<UserCryptoWallet>> => {
    return axios
        .get(`/user-wallet/get-by-user/${uid}`)
        .then(({ data }) => {
            const wallets: UserCryptoWallet[] = data as UserCryptoWallet[];

            return wallets;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getAllCryptoWithAllUserWallet = async (): Promise<any> => {
    return axios
        .get(`/user-wallet/list-detail`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const swap = async (swap: ISwap, translate: any): Promise<AnyAction> => {
    return axios
        .post("/user-wallet/swap", { ...swap })
        .then((res) => {
            const { data } = res;
            if (data.code === "SUCCESS") {
                return Promise.resolve(
                    translate("BACKEND_RESULT_CODE", data.message),
                );
            }
        })
        .catch((err: any) => {
            return Promise.reject(err.response.data);
        });
};

const getUserWalletSolde = async (abbr: string): Promise<number> => {
    return axios
        .get(`/user-wallet/solde/${abbr}`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getChildWalletSolde = async (
    uid: string,
    abbr: string,
): Promise<number> => {
    return axios
        .get(`/user-wallet/${uid}/solde/${abbr}`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getTotalKNT = async (): Promise<number> => {
    return axios
        .get(`/user-wallet/get-total-knt`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getFee = (amount: number, fee_config: FeeConfig) => {
    if (fee_config.type === WithdrawalFeeType.PERCENT) {
        const fee = (amount * fee_config.fee) / 100;
        return fee;
    }
    return fee_config.fee;
};

const convertAmountToUSD = (amount: number, usd_price: number) => {
    const amount_converted = amount * usd_price;
    return amount_converted;
};

const convertAmountUSDToOther = (amount_usd: number, price: number) => {
    const amount_converted = amount_usd / price;
    return amount_converted;
};

const getBonusChartData = async (): Promise<BonusChartData> => {
    return axios
        .get(`/wallet-history/get-bonus-chart-data`)
        .then(({ data }) => {
            const item: BonusChartData = mapBonusChartData(data);

            return item;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const deposit = async (
    deposit: IDeposit,
    translate: any,
    minDeposit?: number,
): Promise<DepositResponse> => {
    if (minDeposit && deposit.amount < minDeposit) {
        return Promise.reject(
            // `A minimum deposit of ${minDeposit} ${deposit.wallet} is required`,
            translate("BACKEND_RESPONSE", "DEPOSIT_MIN_AMOUNT", {
                min_deposit: minDeposit,
                abbreviation: deposit.wallet,
            }),
        );
    }
    return axios
        .post("/deposit", { ...deposit })
        .then((res) => {
            const data = res.data as DepositResponse;
            if (data.code === "ORDER_CREATED" || data.code === "success") {
                return Promise.resolve(data);
            } else {
                return Promise.reject(
                    UtilsService.getAxiosErrorMessage(data.message),
                );
            }
        })
        .catch((err: any) => {
            const error2 = translate(
                "BACKEND_RESPONSE",
                err.response.data.code,
                typeof err.response.data.stack === "object"
                    ? err.response.data.stack
                    : {},
            );

            return Promise.reject(error2);
        });
};

const depositAccountToAccount = async (
    withdraw: TransfertAccount,
    translate: (text: string, text2: string) => string,
): Promise<string> => {
    return axios
        .post("/deposit/transfert", { ...withdraw })
        .then((res) => {
            const { data } = res;

            return Promise.resolve(translate("TRANSFERT_MORE", data.code));
        })
        .catch((err: any) => {
            return Promise.reject(
                translate(
                    "TRANSFERT_MORE",
                    UtilsService.getAxiosErrorMessage(err, true),
                ),
            );
        });
};

const getGlobalBonusChartData = async (): Promise<BonusChartData> => {
    return axios
        .get(`/wallet-history/get-global-bonus-chart-data`)
        .then(({ data }) => {
            const item: BonusChartData = mapBonusChartData(data);

            return item;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getUserWalletNoOwner = async (): Promise<Array<UserCryptoWallet>> => {
    return axios
        .get(`/user-wallet/get-all-no-owner`)
        .then(({ data }) => {
            const wallets: UserCryptoWallet[] = data as UserCryptoWallet[];

            return wallets;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const getBase64Logo = async (crypto_id: number): Promise<string> => {
    return axios
        .get(`/user-wallet/get-base64-logo/${crypto_id}`)
        .then(({ data }) => {
            return data;
        })
        .catch((err: any) => {
            return Promise.reject(UtilsService.getAxiosErrorMessage(err));
        });
};

const WalletService = {
    getUserWallet,
    swap,
    getUserWalletSolde,
    getTotalKNT,
    getFee,
    convertAmountToUSD,
    convertAmountUSDToOther,
    getBonusChartData,
    deposit,
    getAllCryptoWithAllUserWallet,
    getByUserFromAdmin,
    getGlobalBonusChartData,
    getUserWalletWithNetwork,
    getChildWalletSolde,
    getUserWalletNoOwner,
    depositAccountToAccount,
    getBase64Logo,
};

export default WalletService;
